import { useEffect, useState } from 'react';
import { X, ExternalLink } from 'lucide-react';
import { apiAnonGet } from '../services/api-client';
import type { BannerMessage, BannerColor } from '@hemberedskapsappen/shared';

interface BannerDisplayProps {
  location: 'login' | 'dashboard';
  compact?: boolean;
}

const colorStyles: Record<BannerColor, { bg: string; border: string; text: string; button: string }> = {
  yellow: {
    bg: 'bg-[#453c1a]/90',
    border: 'border-[#8c6f2b]/80',
    text: 'text-amber-50',
    button: 'hover:bg-[#453c1a]/80',
  },
  green: {
    bg: 'bg-[#23451a]/90',
    border: 'border-[#4b7a3f]/80',
    text: 'text-green-100',
    button: 'hover:bg-[#23451a]/80',
  },
  red: {
    bg: 'bg-[#451a1a]/90',
    border: 'border-[#8f4a4a]/80',
    text: 'text-white',
    button: 'hover:bg-[#451a1a]/80',
  },
  blue: {
    bg: 'bg-[#1a2245]/90',
    border: 'border-[#4a5a8f]/80',
    text: 'text-blue-100',
    button: 'hover:bg-[#1a2245]/80',
  },
};

export default function BannerDisplay({ location, compact = false }: BannerDisplayProps) {
  const [banners, setBanners] = useState<BannerMessage[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const response = await apiAnonGet(`/banners?location=${location}`);

        if (response.success && response.data) {
          const data = response.data;
          if ('banners' in data && Array.isArray(data.banners)) {
            setBanners(data.banners);
          }
        }
      } catch (error) {
        console.error('Failed to fetch banners:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBanners();
  }, [location]);

  const handleDismiss = (bannerId: string) => {
    setDismissed((prev) => new Set(prev).add(bannerId));
  };

  const visibleBanners = banners.filter((banner) => !dismissed.has(banner.id));

  if (loading || visibleBanners.length === 0) {
    return null;
  }

  return (
    <div className={`w-full ${compact ? 'py-1' : 'py-3'} space-y-2`}>
      {visibleBanners.map((banner) => {
        const styles = colorStyles[banner.color];

        return (
          <div
            key={banner.id}
            className={`relative rounded-lg border ${styles.border} ${styles.bg} p-3 ${styles.text} backdrop-blur-sm animate-in fade-in slide-in-from-top-2 duration-300`}
          >
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-base md:text-lg mb-1 leading-snug">{banner.title}</h3>
                <p className="text-sm md:text-base opacity-90 whitespace-pre-wrap leading-snug">{banner.description}</p>
                {banner.link && (
                  <a
                    href={banner.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-2 text-sm md:text-base underline hover:opacity-80 transition-opacity"
                  >
                    Läs mer
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
              <button
                onClick={() => handleDismiss(banner.id)}
                className={`flex-shrink-0 p-1 rounded-md transition-colors ${styles.button}`}
                aria-label="Stäng meddelande"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
